import React, { useState } from 'react';
import {
  Box, Button, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  Alert, Chip, Divider, Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PeopleAlt as PeopleAltIcon,
  SwapHoriz as SwapHorizIcon,
  PowerSettingsNew as PowerOffIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { customersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const fmtDate = (ts) => {
  if (!ts) return '-';
  return new Date(ts).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });
};

export default function CustomerManagement() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [detailCustomer, setDetailCustomer] = useState(null); // customer object or null
  const [form, setForm] = useState({ name: '', phone: '', email: '', notes: '' });
  const [editForm, setEditForm] = useState({ id: null, name: '', phone: '', email: '', notes: '' });
  const [error, setError] = useState('');

  // Rotate / assign dialog state
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateOldAccountId, setRotateOldAccountId] = useState(null); // DB Account.id
  const [rotateNewAccountId, setRotateNewAccountId] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignAccountId, setAssignAccountId] = useState('');

  const { data: customers = [] } = useQuery('customers', () => customersAPI.getAll().then(r => r.data));

  const { data: detailData } = useQuery(
    ['customer-accounts', detailCustomer?.id],
    () => customersAPI.getAccounts(detailCustomer.id).then(r => r.data),
    { enabled: !!detailCustomer },
  );

  const { data: availableAccounts = [] } = useQuery(
    'customers-available',
    () => customersAPI.getAvailable().then(r => r.data),
    { enabled: rotateOpen || assignOpen },
  );

  const createMutation = useMutation(customersAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('customers');
      setCreateOpen(false); setForm({ name: '', phone: '', email: '', notes: '' }); setError('');
    },
    onError: (e) => setError(e.response?.data?.error || 'Tạo khách hàng thất bại'),
  });

  const updateMutation = useMutation(
    ({ id, data }) => customersAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('customers');
        setEditOpen(false); setError('');
      },
      onError: (e) => setError(e.response?.data?.error || 'Cập nhật thất bại'),
    }
  );

  const deleteMutation = useMutation(customersAPI.delete, {
    onSuccess: () => queryClient.invalidateQueries('customers'),
    onError: (e) => alert(e.response?.data?.error || 'Xóa thất bại'),
  });

  const rotateMutation = useMutation(
    ({ id, oldId, newId }) => customersAPI.rotate(id, oldId, newId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('customers');
        queryClient.invalidateQueries(['customer-accounts', detailCustomer?.id]);
        queryClient.invalidateQueries('customers-available');
        setRotateOpen(false); setRotateNewAccountId(''); setRotateOldAccountId(null);
      },
      onError: (e) => alert(e.response?.data?.error || 'Đổi account thất bại'),
    }
  );

  const assignMutation = useMutation(
    ({ id, accountId }) => customersAPI.assign(id, accountId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('customers');
        queryClient.invalidateQueries(['customer-accounts', detailCustomer?.id]);
        queryClient.invalidateQueries('customers-available');
        setAssignOpen(false); setAssignAccountId('');
      },
      onError: (e) => alert(e.response?.data?.error || 'Gán account thất bại'),
    }
  );

  const retireMutation = useMutation(
    ({ id, accountId }) => customersAPI.retire(id, accountId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('customers');
        queryClient.invalidateQueries(['customer-accounts', detailCustomer?.id]);
        queryClient.invalidateQueries('customers-available');
      },
      onError: (e) => alert(e.response?.data?.error || 'Ngừng account thất bại'),
    }
  );

  if (!isAdmin()) {
    return <Box><Alert severity="info">Bạn không có quyền truy cập trang này.</Alert></Box>;
  }

  const activePeriods = (detailData?.periods || []).filter(p => !p.retired_at);
  const retiredPeriods = (detailData?.periods || []).filter(p => p.retired_at);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Khách hàng</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm({ name: '', phone: '', email: '', notes: '' }); setError(''); setCreateOpen(true); }}>
          Tạo khách hàng
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tên</TableCell>
                <TableCell>Điện thoại</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Tài khoản đang chạy</TableCell>
                <TableCell>Tổng số TK đã dùng</TableCell>
                <TableCell>Ngày tạo</TableCell>
                <TableCell align="right">Thao tác</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center">Chưa có khách hàng nào</TableCell></TableRow>
              ) : customers.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.phone || '-'}</TableCell>
                  <TableCell>{c.email || '-'}</TableCell>
                  <TableCell>
                    <Chip label={c.active_count} color={c.active_count > 0 ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>{c.total_periods}</TableCell>
                  <TableCell>{new Date(c.created_at).toLocaleDateString('vi-VN')}</TableCell>
                  <TableCell align="right">
                    <IconButton color="info" title="Quản lý tài khoản" onClick={() => setDetailCustomer(c)}>
                      <PeopleAltIcon />
                    </IconButton>
                    <IconButton color="primary" title="Sửa" onClick={() => {
                      setEditForm({ id: c.id, name: c.name, phone: c.phone || '', email: c.email || '', notes: c.notes || '' });
                      setError(''); setEditOpen(true);
                    }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" title="Xóa" onClick={() => {
                      if (window.confirm(`Xóa khách hàng "${c.name}"?`)) deleteMutation.mutate(c.id);
                    }}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ─── Create dialog ─── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate(form); }}>
          <DialogTitle>Tạo khách hàng mới</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField margin="normal" required fullWidth label="Tên khách hàng" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <TextField margin="normal" fullWidth label="Điện thoại" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <TextField margin="normal" fullWidth label="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <TextField margin="normal" fullWidth multiline rows={3} label="Ghi chú" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)}>Hủy</Button>
            <Button type="submit" variant="contained" disabled={createMutation.isLoading}>Tạo</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ─── Edit dialog ─── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={(e) => { e.preventDefault(); updateMutation.mutate({ id: editForm.id, data: editForm }); }}>
          <DialogTitle>Sửa khách hàng</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField margin="normal" required fullWidth label="Tên khách hàng" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            <TextField margin="normal" fullWidth label="Điện thoại" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
            <TextField margin="normal" fullWidth label="Email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            <TextField margin="normal" fullWidth multiline rows={3} label="Ghi chú" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>Hủy</Button>
            <Button type="submit" variant="contained" disabled={updateMutation.isLoading}>Lưu</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ─── Detail dialog: account history + rotation ─── */}
      <Dialog open={!!detailCustomer} onClose={() => setDetailCustomer(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Quản lý tài khoản — {detailCustomer?.name}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="subtitle1" fontWeight={600}>
              Đang chạy ({activePeriods.length})
            </Typography>
            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => { setAssignAccountId(''); setAssignOpen(true); }}>
              Thêm account
            </Button>
          </Box>

          {activePeriods.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>Chưa có tài khoản nào đang chạy</Alert>
          ) : (
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>MT5 ID</TableCell>
                    <TableCell>Tên</TableCell>
                    <TableCell>Group</TableCell>
                    <TableCell>Broker</TableCell>
                    <TableCell>Bắt đầu</TableCell>
                    <TableCell align="right">Thao tác</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activePeriods.map((p) => (
                    <TableRow key={p.period_id}>
                      <TableCell>{p.mt5_account_id}</TableCell>
                      <TableCell>{p.name}</TableCell>
                      <TableCell>{p.group_name || '-'}</TableCell>
                      <TableCell>{p.broker_name || '-'}</TableCell>
                      <TableCell>{fmtDate(p.activated_at)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<SwapHorizIcon />}
                            onClick={() => {
                              setRotateOldAccountId(p.account_id);
                              setRotateNewAccountId('');
                              setRotateOpen(true);
                            }}
                          >
                            Đổi
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<PowerOffIcon />}
                            onClick={() => {
                              if (window.confirm(`Ngừng dùng tài khoản ${p.mt5_account_id}?`)) {
                                retireMutation.mutate({ id: detailCustomer.id, accountId: p.account_id });
                              }
                            }}
                          >
                            Ngừng
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
            Đã ngừng ({retiredPeriods.length})
          </Typography>
          {retiredPeriods.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Chưa có lịch sử</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>MT5 ID</TableCell>
                    <TableCell>Tên</TableCell>
                    <TableCell>Group</TableCell>
                    <TableCell>Bắt đầu</TableCell>
                    <TableCell>Kết thúc</TableCell>
                    <TableCell>TK mới</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {retiredPeriods.map((p) => (
                    <TableRow key={p.period_id}>
                      <TableCell>{p.mt5_account_id}</TableCell>
                      <TableCell>{p.name}</TableCell>
                      <TableCell>{p.group_name || '-'}</TableCell>
                      <TableCell>{fmtDate(p.activated_at)}</TableCell>
                      <TableCell>{fmtDate(p.retired_at)}</TableCell>
                      <TableCell>
                        {p.next_mt5_account_id
                          ? <Chip label={`${p.next_mt5_account_id}${p.next_account_name ? ` — ${p.next_account_name}` : ''}`} size="small" color="info" variant="outlined" />
                          : <Typography variant="caption" color="text.secondary">—</Typography>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailCustomer(null)}>Đóng</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Rotate dialog ─── */}
      <Dialog open={rotateOpen} onClose={() => setRotateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Đổi tài khoản</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Tài khoản cũ sẽ được ghi nhận là ngừng dùng. Thao tác không tự tắt Algo — bạn tự tắt trên dashboard sau khi đổi.
          </Alert>
          <FormControl fullWidth margin="normal">
            <InputLabel>Tài khoản mới</InputLabel>
            <Select
              value={rotateNewAccountId}
              label="Tài khoản mới"
              onChange={(e) => setRotateNewAccountId(e.target.value)}
            >
              {availableAccounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.mt5_account_id} — {a.name} {a.group_name ? `(${a.group_name})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotateOpen(false)}>Hủy</Button>
          <Button
            variant="contained"
            disabled={!rotateNewAccountId || rotateMutation.isLoading}
            onClick={() => rotateMutation.mutate({
              id: detailCustomer.id,
              oldId: rotateOldAccountId,
              newId: rotateNewAccountId,
            })}
          >
            Xác nhận đổi
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Assign dialog ─── */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Thêm tài khoản cho khách hàng</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Chọn tài khoản</InputLabel>
            <Select
              value={assignAccountId}
              label="Chọn tài khoản"
              onChange={(e) => setAssignAccountId(e.target.value)}
            >
              {availableAccounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.mt5_account_id} — {a.name} {a.group_name ? `(${a.group_name})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Hủy</Button>
          <Button
            variant="contained"
            disabled={!assignAccountId || assignMutation.isLoading}
            onClick={() => assignMutation.mutate({ id: detailCustomer.id, accountId: assignAccountId })}
          >
            Thêm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
